import React, {memo} from "react";
import PropTypes from 'prop-types';

import CustomPlayerShadowDom from "../PlayerShadowDom/CustomPlayerShadowDom";
// import Player2ShadowDom from "../PlayerShadowDom/Player2ShadowDom";
import PlayerShadowDom from "../PlayerShadowDom/PlayerShadowDom";
import Player2ShadowDomTesting from "../PlayerShadowDom/Player2ShadowDomTesting";

/**
 * @typedef {Object} LtiPlayerProps
 * @property {Object} activity - Activity object containing data and metadata
 * @property {string} activity.data - The activity data as a string
 * @property {string} [activity.app_mount_name] - Mount name for custom apps
 * @property {Array} [activity.app_files] - Files for custom apps
 * @property {string} [activity.title] - Activity title
 * @property {string} [activity.description] - Activity description
 * @property {boolean} [activity.isPlayerV2] - Whether this is a V2 player activity
 * @property {string} channelId - ID of the channel
 * @property {string} activityId - ID of the activity
 * @property {Function} onPlayerCreated - Callback function triggered when player is created used by Legacy Player
 * @property {boolean} [supportsLtiBasicOutcome] - Whether LTI Basic Outcome service is supported
 * @property {Function} [postActivityScore] - Function to send activity score back to LMS
 * @property {Function} [fetchPreviousActivityScore] - Function to retrieve previous activity score
 */

/**
 * LtiPlayer component that renders different player components based on activity type.
 * 
 * @component
 * @param {LtiPlayerProps} props - Component props
 * @returns {React.ReactElement} The appropriate player component based on activity type
 */
const LtiPlayer = memo(({ activity, channelId, activityId, onPlayerCreated, supportsLtiBasicOutcome, postActivityScore, fetchPreviousActivityScore }) => {
  if (activity.data.includes("app_id")) {
    return (
      <CustomPlayerShadowDom
        size="embed"
        playerName={activity.app_mount_name}
        playerAssets={activity.app_files}
        shadowDom={true}
      />
    );
  } else if (activity?.isPlayerV2) {
    const basePlayerOptions = {
      activityData: activity?.data,
      activityTitle: activity?.title,
      activityDescription: activity?.description,
      channelId: channelId,
      activityId: activityId,
      canAuthor: false,
      isNewActivity: false,
    };
    
    const playerOptions = { ...basePlayerOptions };
    
    if (supportsLtiBasicOutcome) {
      playerOptions.postActivityScore = postActivityScore;
      playerOptions.fetchPreviousActivityScore = fetchPreviousActivityScore;
    }
    
    return (
      <Player2ShadowDomTesting
        size="embed"
        playerOptions={playerOptions}
      />
    );
  } else {
    return (
      <div>
        <PlayerShadowDom onPlayerCreated={onPlayerCreated} size="embed" />
      </div>
    );
  }
});

LtiPlayer.displayName = "LtiPlayer";

LtiPlayer.defaultProps = {
  supportsLtiBasicOutcome: false,
  postActivityScore: undefined,
  fetchPreviousActivityScore: undefined
};

LtiPlayer.propTypes = {
  activity: PropTypes.shape({
    data: PropTypes.string.isRequired,
    app_mount_name: PropTypes.string,
    app_files: PropTypes.array,
    title: PropTypes.string,
    description: PropTypes.string,
    isPlayerV2: PropTypes.bool,
  }).isRequired,
  channelId: PropTypes.string.isRequired,
  activityId: PropTypes.string.isRequired,
  onPlayerCreated: PropTypes.func.isRequired,
  supportsLtiBasicOutcome: PropTypes.bool,
  postActivityScore: PropTypes.func,
  fetchPreviousActivityScore: PropTypes.func
};

export default LtiPlayer;